import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Flame, Trophy, Calendar, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Challenges() {
  const [challenges, setChallenges] = useState([]);
  const [mine, setMine] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/challenges"), api.get("/challenges/mine")]);
    setChallenges(a.data); setMine(b.data);
  };
  useEffect(() => { load(); }, []);

  const join = async (id) => {
    try {
      await api.post(`/challenges/${id}/join`);
      toast.success("You're in!");
      navigate(`/challenges/${id}`);
    } catch { toast.error("Couldn't join"); }
  };

  const myMap = new Map(mine.map(m => [m.challenge_id, m]));
  const sponsored = challenges.filter(c => c.sponsor_name);
  const standard = challenges.filter(c => !c.sponsor_name);

  return (
    <div data-testid="challenges-page">
      <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Challenges</h1>
      <p className="text-white/60 mt-1.5">Time-bound quests. Big bonus XP.</p>

      {sponsored.length > 0 && (
        <>
          <div className="flex items-center gap-2 mt-7">
            <span className="text-xs uppercase tracking-widest text-neonyellow font-bold">★ Sponsored</span>
            <div className="flex-1 h-px bg-neonyellow/20"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
            {sponsored.map(c => (
              <ChallengeCard key={c.challenge_id} c={c} progress={myMap.get(c.challenge_id)} onJoin={join} sponsored/>
            ))}
          </div>
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-7">
        {standard.map(c => (
          <ChallengeCard key={c.challenge_id} c={c} progress={myMap.get(c.challenge_id)} onJoin={join}/>
        ))}
      </div>
    </div>
  );
}

function ChallengeCard({ c, progress, onJoin, sponsored }) {
  const joined = !!progress;
  const completed = !!progress?.completed;
  const stepsTotal = (c.steps || []).length;
  const stepsDone = (progress?.done_steps || []).length;
  const submissions = progress?.submissions?.length || 0;

  return (
    <div className={`glass-card p-6 flex flex-col ${sponsored ? "border-neonyellow/30 shadow-[0_0_24px_rgba(255,215,0,0.15)]" : ""}`}
         data-testid={`challenge-card-${c.challenge_id}`}>
      {sponsored && (
        <div className="flex items-center gap-2 mb-3 text-xs">
          {c.sponsor_logo
            ? <img src={c.sponsor_logo} alt={c.sponsor_name} className="h-6 max-w-[100px] object-contain"/>
            : <div className="font-bold text-neonyellow">{c.sponsor_name}</div>}
          <span className="text-white/50">Presents</span>
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${sponsored ? "bg-neonyellow/15 text-neonyellow" : "bg-neonpink/15 text-neonpink"}`}><Flame/></div>
        <div className="flex-1">
          <div className="font-display font-bold text-lg">{c.title}</div>
          <div className="text-xs text-white/55 flex items-center gap-2"><Calendar size={12}/> {c.duration_days} days</div>
        </div>
        <span className="text-xs px-3 py-1 rounded-full bg-neonyellow/15 text-neonyellow font-bold flex items-center gap-1"><Trophy size={12}/> +{c.reward_points}</span>
      </div>
      <p className="text-sm text-white/70 mt-3 flex-1">{c.description}</p>

      {joined && stepsTotal > 0 && (
        <div className="mt-4 flex items-center gap-2" data-testid={`challenge-card-progress-${c.challenge_id}`}>
          <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-neoncyan to-neongreen transition-all"
                 style={{ width: `${completed ? 100 : Math.round((stepsDone / stepsTotal) * 100)}%` }}/>
          </div>
          <span className="text-xs text-white/50 font-bold whitespace-nowrap">
            {completed ? "Done ✓" : `${stepsDone}/${stepsTotal}`}
          </span>
        </div>
      )}

      {completed ? (
        <div className="mt-4 flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 text-neongreen font-bold">
            <CheckCircle2 size={14}/> Completed
            {submissions > 0 && <span className="text-white/50 font-normal">· {submissions} upload{submissions === 1 ? "" : "s"}</span>}
          </span>
          <Link to={`/challenges/${c.challenge_id}`}
                data-testid={`view-challenge-${c.challenge_id}`}
                className="text-neoncyan hover:text-neonyellow flex items-center gap-1 transition">
            View <ArrowRight size={12}/>
          </Link>
        </div>
      ) : joined ? (
        <Link to={`/challenges/${c.challenge_id}`}
              data-testid={`view-challenge-${c.challenge_id}`}
              className="mt-4 btn-secondary-neon text-sm w-full flex items-center justify-center gap-1">
          Continue <ArrowRight size={14}/>
        </Link>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Link to={`/challenges/${c.challenge_id}`}
                data-testid={`view-challenge-${c.challenge_id}`}
                className="btn-secondary-neon text-sm text-center">
            Details
          </Link>
          <button onClick={() => onJoin(c.challenge_id)}
            data-testid={`join-challenge-${c.challenge_id}`}
            className="btn-primary-neon text-sm">
            Join
          </button>
        </div>
      )}
    </div>
  );
}
